import { ReactNode, useEffect, useState } from 'react';
import { useFeatureUsage } from '../hooks/useUsageLimits';
import { FeatureKey } from '../types/subscription';
import { LimitReachedModal } from './LimitReachedModal';

interface LimitGuardProps {
  feature: FeatureKey;
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * LimitGuard Component
 * 
 * Guards features behind usage limits, showing a blocking modal when limits are reached.
 * 
 * Features:
 * - Optimistically renders children during loading
 * - Shows blocking modal when hasCapacity is false
 * - Supports custom fallback UI
 * - Full i18n support
 * - Accessibility: ESC key to dismiss, focus trap
 * 
 * @example
 * ```tsx
 * <LimitGuard feature="private_prompts">
 *   <button onClick={createPrompt}>Create Prompt</button>
 * </LimitGuard>
 * ```
 */
export function LimitGuard({ feature, children, fallback }: LimitGuardProps) {
  const usage = useFeatureUsage(feature);
  const [isModalVisible, setIsModalVisible] = useState(true);

  // Reset modal visibility when usage changes
  useEffect(() => {
    if (usage && !usage.hasCapacity) {
      setIsModalVisible(true);
    }
  }, [usage]);

  const handleClose = () => {
    setIsModalVisible(false);
  };

  // Optimistically render children during loading (Scenario 6)
  if (!usage || usage.hasCapacity) {
    return <>{children}</>;
  }

  // If modal is closed, render children (user can still see the form but can't submit)
  if (!isModalVisible) {
    return <>{children}</>;
  }

  // Use custom fallback if provided (Scenario 4)
  if (fallback) {
    return <>{fallback}</>;
  }

  // Default blocking modal (Scenario 3)
  // Note: If we reach here, usage.limit_value should not be null (hasCapacity is false)
  // But we handle null case for type safety
  if (!usage || usage.limit_value === null) {
    return <>{children}</>;
  }

  return (
    <>
      {children}
      <LimitReachedModal
        feature={feature}
        currentUsage={usage.current_usage}
        limitValue={usage.limit_value}
        onClose={handleClose}
        isVisible={isModalVisible}
      />
    </>
  );
}
